import cn from 'classnames'
import React, { useState } from 'react'
import Timer from 'react-compound-timer/build'

import { enableLoginWithUsername } from '@flowwai-frontend/bps-officer-web/common/data-access'
import { OTPBoxesField } from '@flowwai-frontend/bps-officer-web/common/ui'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import { Button, Hierarchy, SubVariant } from '@flowwai-frontend/design-system'
import { Loader, useCustomT } from '@flowwai-frontend/shared/common-components'

import {
   loginMachineEvents,
   loginMachineTags,
   RESEND_TIME_IN_SECONDS
} from '../../constants/loginConstants'
import {
   LoginMachineSend,
   LoginMachineState
} from '../../machines/LoginMachine'

import './index.css'
import { OTPStep } from './OTPStep'
import * as Styles from './styles'

interface OTPFieldWithResendProps {
   numInputs: number
   loginMachineState: LoginMachineState
   loginMachineSend: LoginMachineSend
   otpValue: string
   setOTPValue: (value: string) => void
   inputClassName?: string
   inputFocusClassName?: string
   errorClassName?: string
   containerStyle?: string
   labelStyles?: string
   label?: string
   disabled?: boolean
   currentStep?: string
   timerRef: React.MutableRefObject<{
      start: () => void
   } | null>
}

const OTPFieldWithResend = (
   props: OTPFieldWithResendProps
): React.ReactElement => {
   const {
      inputClassName,
      inputFocusClassName,
      errorClassName,
      numInputs,
      disabled = false,
      containerStyle,
      labelStyles,
      currentStep,
      loginMachineState,
      loginMachineSend,
      otpValue,
      setOTPValue,
      timerRef
   } = props

   const { errorMessage } = loginMachineState.context

   const t = useCustomT('auth:login.verifyOTP')

   const [isTimeOut, setIsTimeOut] = useState(false)

   const { theme } = useAppTheme()
   const { otpWithResendField: otpResendTheme } = theme.auth.login.otp
   const { otpInputs: otpInputTheme } = theme.auth.login

   const timer = RESEND_TIME_IN_SECONDS

   const getCurrentStepOfOtp = (): string => {
      if (currentStep) {
         return currentStep
      }
      return ''
   }

   const isResendOTPEnabled = isTimeOut

   const toggleOtpTimeout = (): void => {
      setIsTimeOut(!isTimeOut)
   }

   const toggleOtpTimeoutCallBack = (): void => {
      setIsTimeOut(!isTimeOut)
   }

   const onClickResend = (reset: () => void): void => {
      if (enableLoginWithUsername) {
         loginMachineSend(loginMachineEvents.resendUsernameOtp)
      } else {
         loginMachineSend(loginMachineEvents.resendEmailOtp)
      }
      toggleOtpTimeout()
      reset()
   }

   const onResend = (reset: () => void): void => {
      isResendOTPEnabled && onClickResend(reset)
   }

   const onResetError = (): void => {
      loginMachineSend(loginMachineEvents.clearErrorMessage)
   }

   const getResendOTPButton = (reset: () => void): React.ReactElement => {
      const shouldShowLoader = loginMachineState.hasTag(
         loginMachineTags.resendOtpLoading
      )

      return shouldShowLoader ? (
         <Loader className={cn(Styles.loaderStyles)} width={16} height={16} />
      ) : (
         <div className={cn(Styles.resendOTPAndCallContainerStyles)}>
            <Button
               hierarchy={Hierarchy.Link}
               type='button'
               size='Small'
               subVariant={SubVariant.Primary}
               className={cn(
                  Styles.resendOTPTextContainerStyles,
                  {
                     'cursor-pointer': isResendOTPEnabled
                  },
                  { 'cursor-not-allowed': !isResendOTPEnabled }
               )}
               onClick={() => onResend(reset)}
            >
               <span
                  className={cn(
                     Styles.resendOtpTextStyles,
                     {
                        [otpResendTheme.resendOTPActiveTextColor]: isResendOTPEnabled
                     },
                     {
                        [otpResendTheme.resendOTPDisabledTextColor]: !isResendOTPEnabled
                     }
                  )}
               >
                  {t('resend')}
               </span>
            </Button>
         </div>
      )
   }

   const shouldShowTimer =
      !isResendOTPEnabled &&
      !loginMachineState.hasTag(loginMachineTags.resendOtpLoading)

   return (
      <Timer
         initialTime={timer * 1000}
         direction='backward'
         formatValue={value => `${value < 10 ? `0${value}` : value}`}
         checkpoints={[
            {
               time: 0,
               callback: () => toggleOtpTimeoutCallBack()
            }
         ]}
      >
         {({ reset, start }: { reset: () => void; start: () => void }) => {
            timerRef.current = { start }
            return (
               <OTPStep reset={reset} currentSep={getCurrentStepOfOtp()}>
                  <div className={cn(Styles.otpFieldContainerStyles)}>
                     <div className={cn(Styles.otpBoxesFieldContainerStyles)}>
                        <OTPBoxesField
                           disabled={disabled}
                           errorMsg={errorMessage ?? undefined}
                           numInputs={numInputs}
                           inputClassName={
                              inputClassName
                                 ? inputClassName
                                 : cn(
                                      Styles.otpInputStyles,
                                      otpInputTheme.otpInputBGColor,
                                      otpInputTheme.otpInputDisabledBorderColor,
                                      otpInputTheme.otpInputTextColor
                                   )
                           }
                           inputFocusClassName={
                              inputFocusClassName
                                 ? inputFocusClassName
                                 : cn(
                                      Styles.otpInputFocusStyles,
                                      otpInputTheme.otpInputFocusBGColor,
                                      otpInputTheme.otpInputFocusBorderColor
                                   )
                           }
                           errorClassName={
                              errorClassName
                                 ? errorClassName
                                 : cn(
                                      Styles.otpErrorStyles,
                                      otpInputTheme.otpInputErrorBorderColor,
                                      otpInputTheme.otpErrorInputTextColor
                                   )
                           }
                           label={t('otpInputLabel')}
                           containerStyle={
                              containerStyle
                                 ? containerStyle
                                 : 'container-styles'
                           }
                           labelStyles={labelStyles}
                           otpValue={otpValue}
                           setOTPValue={setOTPValue}
                           onResetError={onResetError}
                        />
                     </div>
                     <div
                        className={cn(
                           Styles.resendOTPWithOTPTimerContainerStyles
                        )}
                     >
                        {getResendOTPButton(reset)}
                        {shouldShowTimer ? (
                           <span
                              className={cn(
                                 Styles.otpTimerTextStyles,
                                 otpResendTheme.otpTimerTextColor
                              )}
                           >
                              <Timer.Minutes />
                              :<Timer.Seconds />
                           </span>
                        ) : (
                           <span
                              className={cn(
                                 Styles.otpTimerTextStyles,
                                 otpResendTheme.disabledOtpTimerTextColor
                              )}
                           >
                              {t('timerFinalState')}
                           </span>
                        )}
                     </div>
                  </div>
               </OTPStep>
            )
         }}
      </Timer>
   )
}

export { OTPFieldWithResend }
